import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useRoute } from "wouter";
import BrandLockup from "@/components/BrandLockup";
import { getVoicePage } from "@/content/voicePages";
import { formatRecordingDuration } from "@/lib/recording";
import { friendlyErrorMessage } from "@/lib/friendlyError";
import { isValidShareToken } from "@/lib/shareToken";
import { supabase } from "@/lib/supabase";

type PlaybackResponse = {
  page_number: string;
  duration_seconds: number;
  audio_url: string;
  expires_in: number;
};

async function describeFunctionError(error: unknown): Promise<string> {
  if (error && typeof error === "object" && "context" in error) {
    const context = (error as { context?: unknown }).context;
    if (context instanceof Response) {
      try {
        const body = await context.json() as { error?: unknown };
        if (body.error === "This story is no longer shared.") return body.error;
        if (body.error === "Audio is temporarily unavailable. Please try again later.") return body.error;
      } catch { /* Use the generic message below. */ }
    }
  }
  return "Audio is temporarily unavailable. Please try again later.";
}

export default function SharePage() {
  const [, params] = useRoute<{ token: string }>("/s/:token");
  const token = params?.token ?? "";
  const [playback, setPlayback] = useState<PlaybackResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackPosition, setPlaybackPosition] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const loadPlayback = useCallback(async () => {
    setPlayback(null);
    setError(null);
    setIsLoading(true);
    if (!isValidShareToken(token)) {
      setError("This story is no longer shared.");
      setIsLoading(false);
      return;
    }
    if (!supabase) {
      setError("Audio is temporarily unavailable. Please try again later.");
      setIsLoading(false);
      return;
    }

    try {
      const { data, error: functionError } = await supabase.functions.invoke("share-playback", {
        body: { token },
      });
      if (functionError) throw new Error(await describeFunctionError(functionError));
      const response = data as Partial<PlaybackResponse> | null;
      if (!response || typeof response.page_number !== "string" ||
          typeof response.duration_seconds !== "number" || typeof response.audio_url !== "string") {
        throw new Error("This story is no longer shared.");
      }
      setPlayback(response as PlaybackResponse);
    } catch (requestError) {
      setError(friendlyErrorMessage(requestError, "share", "Audio is temporarily unavailable. Please try again later."));
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => { void loadPlayback(); }, [loadPlayback]);

  useEffect(() => {
    setIsPlaying(false);
    setPlaybackPosition(0);
  }, [playback?.audio_url]);

  const togglePlayback = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;
    setError(null);
    if (!audio.paused) {
      audio.pause();
      return;
    }
    try {
      await audio.play();
    } catch {
      setError("The audio link expired or could not be loaded. Tap below to refresh it.");
    }
  }, []);

  const page = playback ? getVoicePage(playback.page_number) : undefined;

  return (
    <div className="paper-shell inner-shell">
      <header className="site-header gift-header">
        <BrandLockup className="gift-wordmark" />
      </header>

      <main className="callback-layout share-layout" aria-live="polite">
        {isLoading ? (
          <section className="callback-card share-card">
            <span className="loading-mark" aria-hidden="true" />
            <p className="eyebrow"><span className="eyebrow-rule" /> A voice story</p>
            <h1>Opening this<br />story<span className="title-period">.</span></h1>
            <p className="form-description">Getting the audio ready to play.</p>
          </section>
        ) : !page || !playback ? (
          <section className="form-card callback-card share-card" role="status">
            <p className="eyebrow"><span className="eyebrow-rule" /> A voice story</p>
            <h1>{error === "This story is no longer shared."
              ? <>This story is<br />no longer shared<span className="title-period">.</span></>
              : <>Audio is temporarily<br />unavailable<span className="title-period">.</span></>}</h1>
            <p className="form-description">{error === "This story is no longer shared."
              ? "Ask the person who sent it to share it again."
              : error ?? "Ask the person who sent it to share it again."}</p>
            {error && error !== "This story is no longer shared." && (
              <button className="secondary-action full-width share-action" type="button" onClick={() => void loadPlayback()}>
                Try again
              </button>
            )}
          </section>
        ) : (
          <section className="form-card callback-card share-card" aria-labelledby="shared-story-title">
            <p className="eyebrow"><span className="eyebrow-rule" /> {page.number} · {page.chapterTitle}</p>
            <h1 id="shared-story-title">{page.prompt}</h1>
            <p className="share-page-label">Voice page {page.number}</p>
            <p className="share-play-label">A voice story from someone you love</p>
            <button
              className="primary-action full-width share-play-button"
              type="button"
              onClick={() => void togglePlayback()}
              aria-label={isPlaying ? "Pause story" : playbackPosition > 0 ? "Resume story" : "Play story"}
            >
              {isPlaying ? "Pause story" : playbackPosition > 0 ? "Resume story" : "Play story"}
              <span aria-hidden="true">{isPlaying ? "Ⅱ" : "▶"}</span>
            </button>
            <audio
              ref={audioRef}
              className="share-audio"
              aria-label="Shared voice story audio"
              playsInline
              preload="metadata"
              src={playback.audio_url}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onTimeUpdate={(event) => setPlaybackPosition(event.currentTarget.currentTime)}
              onEnded={() => { setIsPlaying(false); setPlaybackPosition(0); }}
              onError={() => setError("The audio link expired or could not be loaded. Tap below to refresh it.")}
            >
              Your browser can’t play this audio recording.
            </audio>
            <p className="recording-meta">{formatRecordingDuration(playback.duration_seconds)}</p>
            {error && <p className="error-message" role="alert">{error}</p>}
            <button className="secondary-action full-width share-action" type="button" onClick={() => void loadPlayback()}>
              Get a fresh audio link
            </button>
          </section>
        )}
      </main>

      <footer className="site-footer">
        <nav className="legal-footer-links" aria-label="Legal">
          <Link href="/privacy">Privacy Policy</Link>
          <Link href="/terms">Terms</Link>
        </nav>
        <span>Shared with you from Home, Twice · Kwaderno &amp; Co.</span>
      </footer>
    </div>
  );
}
